import { useCallback, useEffect, useState } from 'react';
import { Button, Dialog, Field, Icon, Input, Select, useErrorDialog, useToast } from '@dayon/design-system';
import type { GameBrandChangeReason, SetGamesBrandsResult } from '@dayon/contracts';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import { BRAND_REASONS, brandRefusalTitle } from './game-brands.ts';
import { NOTE_MAX, noteError, noteIsValid } from './reason-note.ts';

/**
 * ONE CHIP SET, MANY TITLES — the bulk half of brand availability.
 *
 * WHY IT IS A DIFFERENT DIALOG FROM THE DRAWER'S. The per-title dialog names
 * the pairs that move, because an operator can read three chips and check them.
 * Forty titles times three brands is a hundred and twenty pairs, which nobody
 * checks; so this one asks a narrower question it CAN state completely —
 * "add these titles to these shelves, or take them off" — and reports what
 * actually moved afterwards.
 *
 * ONE DIRECTION, NOT A PER-BRAND MIX. The route accepts a per-brand enabled
 * flag, and this screen deliberately does not offer one: a bulk submission that
 * turned brand A on and brand B off for forty titles is a change nobody can
 * hold in their head before pressing it. Two passes say the same thing and each
 * is reviewable.
 *
 * BRANDS NOT TICKED ARE LEFT ALONE. They are not sent, and the route only ever
 * touches the pairs it is given — so this cannot quietly clear a shelf the
 * operator was not looking at.
 */
export function BulkBrandsDialog({
  open,
  gameIds,
  gameNames,
  brands,
  holdsKey,
  mfa,
  onClose,
  onChanged,
}: {
  open: boolean;
  gameIds: string[];
  /** For the sentence at the top; the first few, then a count. */
  gameNames: string[];
  brands: { id: string; name: string }[];
  holdsKey: boolean;
  mfa: boolean;
  onClose: () => void;
  onChanged: (result: SetGamesBrandsResult) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [direction, setDirection] = useState<'ON' | 'OFF'>('ON');
  const [reasonCode, setReasonCode] = useState<GameBrandChangeReason>('CATALOGUE_LAUNCH');
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* Every opening starts clean: brands ticked for one selection must not be
     waiting for the next. */
  useEffect(() => {
    if (!open) return;
    setPicked(new Set());
    setDirection('ON');
    setReasonCode('CATALOGUE_LAUNCH');
    setNote('');
    setNoteTouched(false);
  }, [open]);

  const ready = picked.size > 0 && noteIsValid(note) && gameIds.length > 0;

  const submit = useCallback(async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      const result = await api.put<SetGamesBrandsResult>('/api/games/brands', {
        gameIds,
        brands: [...picked].map((brandId) => ({ brandId, enabled: direction === 'ON' })),
        reasonCode,
        note: note.trim(),
      });
      /* What MOVED, not what was selected — the number the route worked out. */
      toast(
        `${String(result.gamesChanged)} title${result.gamesChanged === 1 ? '' : 's'} ${
          direction === 'ON' ? 'added to' : 'taken off'
        } ${String(picked.size)} shelf${picked.size === 1 ? '' : 'ves'}` +
          (result.gamesUnchanged > 0
            ? `. ${String(result.gamesUnchanged)} already had that setting.`
            : '.'),
      );
      onChanged(result);
      onClose();
    } catch (error: unknown) {
      const raw = error instanceof ApiRequestError ? error.code : undefined;
      showError({
        title: brandRefusalTitle(isErrorCode(raw) ? raw : undefined, `${String(gameIds.length)} titles`, {
          allowed: holdsKey,
          mfa,
        }),
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(raw ? { code: raw } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  }, [ready, gameIds, picked, direction, reasonCode, note, holdsKey, mfa, onChanged, onClose, showError, toast]);

  const subject =
    gameNames.length <= 3
      ? gameNames.join(', ')
      : `${gameNames.slice(0, 3).join(', ')} and ${String(gameNames.length - 3)} more`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Change the shelf for ${String(gameIds.length)} title${gameIds.length === 1 ? '' : 's'}?`}
      dismissible={!submitting}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={submitting}
            disabled={!ready || submitting}
            onClick={() => void submit()}
          >
            {direction === 'ON' ? 'Add to these shelves' : 'Take off these shelves'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            <strong>This decides who can SEE these titles, not whether they work.</strong> A title
            taken off a brand drops out of that brand&rsquo;s catalogue at the next request. Each
            title&rsquo;s own status and its provider&rsquo;s are untouched, so one that is disabled
            stays unopenable wherever it is on. <strong>Brands you do not tick are left exactly as
            they are.</strong>
          </div>
        </div>

        <div className="text-subtle" style={{ fontSize: '12.5px' }}>
          {subject}
        </div>

        <Field label="Do what" hint="One direction for the whole selection — a bulk that mixed both is one nobody can check before pressing it.">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={direction}
              onChange={(e) => setDirection(e.target.value as 'ON' | 'OFF')}
            >
              <option value="ON">Add these titles to the brands below</option>
              <option value="OFF">Take these titles off the brands below</option>
            </Select>
          )}
        </Field>

        <div>
          <div className="panel-title">Brands</div>
          {/* Toggle buttons, not the Chips component: Chips is a RADIOGROUP and
              picks exactly one. Brands here are a multi-select, and the drawer
              already renders per-brand toggles this way. */}
          <div className="row" role="group" aria-label="Brands to change">
            {brands.map((b) => {
              const on = picked.has(b.id);
              return (
                <Button
                  key={b.id}
                  {...(on ? ({ variant: 'primary' } as const) : {})}
                  aria-pressed={on}
                  aria-label={`${b.name}: ${on ? 'selected' : 'not selected'}`}
                  onClick={() =>
                    setPicked((prev) => {
                      const next = new Set(prev);
                      if (next.has(b.id)) next.delete(b.id);
                      else next.add(b.id);
                      return next;
                    })
                  }
                >
                  {b.name}
                </Button>
              );
            })}
          </div>
          {picked.size === 0 && (
            <div className="text-subtle" style={{ fontSize: '12px', marginTop: 'var(--space-1)' }}>
              Tick at least one brand. Nothing happens to the brands you leave alone.
            </div>
          )}
        </div>

        <Field
          label="Reason"
          hint="A closed set, because the Configuration log is a report as well as a story."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value as GameBrandChangeReason)}
            >
              {BRAND_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note"
          error={noteError(note, noteTouched)}
          hint="At least 10 characters. The code says what kind of thing happened; the note says what actually did."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={note}
              maxLength={NOTE_MAX}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => setNoteTouched(true)}
              placeholder="Opening SA Gaming’s tables on FairPlay after the catalogue import."
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
